import {
  Box,
  FormHelperText,
  FormHelperTextProps,
  InputLabel,
  InputLabelProps,
} from "@mui/material";
import { DatePicker, DateTimeValidationError } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import {
  Control,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from "react-hook-form";

import theme, { palette } from "~frontend/theme";

export function RhfDatePicker<T extends FieldValues>({
  name,
  label,
  minDate,
  minDateError,
  maxDate,
  maxDateError,
  timezone,
  fullWidth,
  helperText,
  format, // Format reference here: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
  onError,
  disabled,
  customError,
  inputLabelProps,
  helperTextProps,
  control,
}: {
  name: Path<T>;
  label: string;
  minDate?: DateTime;
  minDateError?: string;
  maxDate?: DateTime;
  maxDateError?: string;
  timezone?: string;
  fullWidth?: boolean;
  helperText?: string;
  format?: string;
  onError?: any;
  disabled?: boolean;
  customError?: { errorText: string };
  inputLabelProps?: InputLabelProps;
  helperTextProps?: FormHelperTextProps;
  control: Control<T>;
}): ReactElement {
  const { clearErrors } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
  });

  const maxMinErrorString = (error: DateTimeValidationError) => {
    switch (error) {
      case "minDate":
        return minDateError;
      case "maxDate":
        return maxDateError;
      default:
        return "";
    }
  };

  const error = customError ? customError.errorText : fieldState.error;

  return (
    <Box sx={{ width: fullWidth ? "100%" : undefined }}>
      {label ? (
        <InputLabel shrink {...inputLabelProps}>
          {label}
        </InputLabel>
      ) : null}
      <DatePicker
        value={
          typeof field.value === "string"
            ? DateTime.fromISO(field.value)
            : field.value
        }
        onChange={field.onChange}
        timezone={timezone}
        onError={
          onError
            ? onError
            : async newError => {
                if (newError) {
                  control.setError(field.name, {
                    message: maxMinErrorString(newError),
                  });
                } else {
                  clearErrors(field.name);
                }
              }
        }
        minDate={minDate}
        maxDate={maxDate}
        sx={{
          borderRadius: 0,
          paddingTop: label ? 0 : undefined,
          width: fullWidth ? "100%" : undefined,
          marginBottom: 0,
          "& .MuiInputAdornment-root": {
            width: "48px",
            justifyContent: "flex-end",
          },
          "& .MuiPickersInputBase-root": {
            borderRadius: 0,
            backgroundColor: palette.white,
          },
        }}
        format={format}
        disabled={disabled}
      />
      {(fieldState.isTouched && error) || helperText ? (
        <FormHelperText
          sx={{
            paddingLeft: theme.spacing(2),
            paddingTop: 0,
            marginTop: 0,
            fontSize: "10px",
          }}
          error={fieldState.isTouched && !!error}
          {...helperTextProps}
        >
          {(fieldState.isTouched && error?.toString()) || helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
}
